// 안전한 API 호출을 위한 래퍼 함수
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getSafeApiUrl } from '../config/environment';

export type ApiCallType = 'GATEWAY' | 'INVENTORY' | 'ORDER' | 'ADMIN';

interface ApiCallLog {
  id: string;
  endpoint: string;
  method: string;
  statusCode: number;
  responseTime: number;
  timestamp: string;
  userId?: string;
  userAgent: string;
  ipAddress: string;
  requestSize?: number;
  responseSize?: number;
  errorMessage?: string;
  pathDuplication?: boolean;
  correctedPath?: string;
}

// API 호출 로그를 저장할 배열 (실제로는 서버로 전송)
const apiCallLogs: ApiCallLog[] = [];

// 경로 중복 감지 함수
const detectPathDuplication = (url: string): { hasDuplication: boolean; correctedPath?: string } => {
  const apiPattern = /\/api\/api\//;
  if (apiPattern.test(url)) {
    const correctedPath = url.replace(/\/api\/api\//g, '/api/');
    return { hasDuplication: true, correctedPath };
  }
  return { hasDuplication: false };
};

// API 호출 로그 전송 함수 (프로덕션에서 비활성화)
const sendApiCallLog = async (log: ApiCallLog) => {
  // 🔴 프로덕션에서 서버 전송 비활성화 (네트워크 에러 방지)
  // 모든 환경에서 로컬 저장만 수행
  apiCallLogs.push(log);
  if (apiCallLogs.length > 1000) {
    apiCallLogs.shift(); // 최대 1000개만 유지
  }
  
  // TODO: 모니터링 로그 서버 전송이 필요한 경우
  // 1. CORS 설정 확인
  // 2. 백엔드 재시작 확인
  // 3. Nginx 설정 확인
  // 위 항목 해결 후 아래 코드 활성화
  
  /* 서버 전송 코드 (비활성화)
  const isProduction = window.location.hostname.includes('judam.kr');
  
  if (!isProduction) {
    return; // 개발 환경에서는 로컬 저장만
  }

  setTimeout(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch('/api/monitoring/logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(log),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      // 실패 시 아무것도 하지 않음 (이미 로컬에 저장됨)
    }
  }, 0);
  */
};

export class SafeApiCaller {
  private apiInstance: AxiosInstance;
  private apiType: ApiCallType;

  constructor(apiType: ApiCallType = 'GATEWAY') {
    this.apiType = apiType;
    
    const baseURL = getSafeApiUrl(apiType === 'ADMIN' ? 'GATEWAY' : apiType);
    this.apiInstance = axios.create({
      baseURL,
      timeout: 30000, // 30초로 증가 (이메일 전송 시간 고려)
    });

    // 요청 인터셉터
    this.apiInstance.interceptors.request.use(
      (config) => {
        const startTime = Date.now();
        (config as any).startTime = startTime;
        
        // 토큰 헤더 추가
        if (this.apiType === 'ADMIN') {
          const adminToken = localStorage.getItem('adminToken');
          if (adminToken) {
            config.headers.Authorization = `Bearer ${adminToken}`;
            console.log('SafeApiCaller - 관리자 토큰 헤더 추가:', adminToken.substring(0, 20) + '...');
          } else {
            console.log('SafeApiCaller - 관리자 토큰 없음');
          }
        } else {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('SafeApiCaller - 일반 토큰 헤더 추가:', token.substring(0, 20) + '...');
          }
        }
        
        // 경로 중복 감지
        const fullUrl = (config.baseURL || '') + (config.url || '');
        const duplicationCheck = detectPathDuplication(fullUrl);
        
        if (duplicationCheck.hasDuplication) {
          console.warn('🚨 경로 중복 감지:', {
            original: fullUrl,
            corrected: duplicationCheck.correctedPath
          });
          
          // 경로 자동 수정
          if (config.url) {
            config.url = config.url.replace(/^\/api\/api\//, '/api/');
          }
        }

        console.log(`🚀 API 호출: ${config.method?.toUpperCase()} ${fullUrl}`);
        return config;
      },
      (error) => {
        console.error('❌ API 요청 오류:', error);
        return Promise.reject(error);
      }
    );

    // 응답 인터셉터
    this.apiInstance.interceptors.response.use(
      (response) => {
        const endTime = Date.now();
        const startTime = (response.config as any).startTime;
        const responseTime = endTime - startTime;
        
        // API 호출 로그 생성
        const log: ApiCallLog = {
          id: `${Date.now()}-${Math.random()}`,
          endpoint: response.config.url || '',
          method: response.config.method || 'GET',
          statusCode: response.status,
          responseTime,
          timestamp: new Date().toISOString(),
          userId: localStorage.getItem('userId') || undefined,
          userAgent: navigator.userAgent,
          ipAddress: 'client-side', // 실제로는 서버에서 IP 추출
          requestSize: JSON.stringify(response.config.data || {}).length,
          responseSize: JSON.stringify(response.data || {}).length,
          pathDuplication: detectPathDuplication((response.config.baseURL || '') + (response.config.url || '')).hasDuplication,
          correctedPath: detectPathDuplication((response.config.baseURL || '') + (response.config.url || '')).correctedPath,
        };

        // 로그 전송
        sendApiCallLog(log);

        console.log(`✅ API 응답: ${response.status} (${responseTime}ms)`);
        return response;
      },
      (error) => {
        const endTime = Date.now();
        const startTime = (error.config as any)?.startTime || endTime;
        const responseTime = endTime - startTime;
        
        // 오류 로그 생성
        const log: ApiCallLog = {
          id: `${Date.now()}-${Math.random()}`,
          endpoint: error.config?.url || '',
          method: error.config?.method || 'GET',
          statusCode: error.response?.status || 0,
          responseTime,
          timestamp: new Date().toISOString(),
          userId: localStorage.getItem('userId') || undefined,
          userAgent: navigator.userAgent,
          ipAddress: 'client-side',
          errorMessage: error.message,
          pathDuplication: detectPathDuplication(error.config?.baseURL + error.config?.url || '').hasDuplication,
          correctedPath: detectPathDuplication(error.config?.baseURL + error.config?.url || '').correctedPath,
        };

        // 오류 로그 전송
        sendApiCallLog(log);

        console.error(`❌ API 오류: ${error.response?.status || 'NETWORK'} (${responseTime}ms)`, error.message);
        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.apiInstance.get(endpoint, config);
  }

  async post<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.apiInstance.post(endpoint, data, config);
  }

  async put<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.apiInstance.put(endpoint, data, config);
  }

  async delete<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.apiInstance.delete(endpoint, config);
  }

  async patch<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.apiInstance.patch(endpoint, data, config);
  }

  // 로컬 로그 조회 (개발용)
  getLocalLogs(): ApiCallLog[] {
    return [...apiCallLogs];
  }

  // 로그 초기화
  clearLocalLogs(): void {
    apiCallLogs.length = 0;
  }
}

// 싱글톤 인스턴스들
export const gatewayApi = new SafeApiCaller('GATEWAY');
export const inventoryApi = new SafeApiCaller('INVENTORY');
export const orderApi = new SafeApiCaller('ORDER');
export const adminApi = new SafeApiCaller('ADMIN');

// safeApiCall 객체 export
export const safeApiCall = {
  gateway: gatewayApi,
  inventory: inventoryApi,
  order: orderApi,
  admin: adminApi,
};

// 전역에서 접근 가능하도록 설정
(window as any).apiCaller = {
  gateway: gatewayApi,
  inventory: inventoryApi,
  order: orderApi,
  admin: adminApi,
  getLocalLogs: () => apiCallLogs,
  clearLogs: () => { apiCallLogs.length = 0; }
}; 